import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import { productAttributesEndpoint } from './use-get-attribute-dictionary-api';

interface Params {
  valueId: number;
  catalogCategoryIds: number[];
}

type TData = number[];
type TError = AxiosError;
type TVariables = Params;

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

// PUT ums/v1/cms/master-product-attributes/values/:valueId/categories
export function useReplaceAttributeValueCategoriesApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ valueId, catalogCategoryIds }: TVariables): Promise<TData> => {
    try {
      const response = await http.put(`${productAttributesEndpoint}/values/${valueId}/categories`, {
        catalogCategoryIds,
      });
      return response.data.data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${productAttributesEndpoint}/values/categories`],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([`${productAttributesEndpoint}/types`]);
      queryClient.invalidateQueries([`${productAttributesEndpoint}/dictionary`]);
      // Keeps the per-value categories query fresh too, symmetric with the
      // type path's invalidation above.
      queryClient.invalidateQueries([`${productAttributesEndpoint}/values`]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
